import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, Check, Loader2, SkipForward } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/api/client';

/**
 * Resolve every flagged address before anything is bought.
 *
 * This screen is the whole point of validating up front. The original app found
 * bad addresses partway through a run, so the shop ended up with half an order
 * shipped and a pile of cleanup. Here nothing is purchased until this list is
 * empty.
 */
export default function ValidationReview({ batch, onReady }) {
  const queryClient = useQueryClient();
  const flagged = batch.items.filter((item) => item.status === 'NEEDS_REVIEW');

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['batch', batch.id] });

  const resolveMutation = useMutation({
    mutationFn: ({ itemId, body }) =>
      api.request(`/batches/${batch.id}/items/${itemId}`, { method: 'PATCH', body }),
    onSuccess: invalidate,
    onError: (error) => toast.error(error.message ?? 'Could not update that row'),
  });

  if (flagged.length === 0) {
    return (
      <Card className="border-none shadow-lg">
        <CardContent className="p-8 text-center space-y-4">
          <Check className="w-12 h-12 mx-auto text-green-600" />
          <div>
            <h3 className="text-lg font-semibold text-slate-900">All addresses check out</h3>
            <p className="text-slate-600">
              {batch.items.filter((item) => item.status === 'READY').length} ready to ship
              {batch.items.filter((item) => item.status === 'SKIPPED').length > 0 &&
                `, ${batch.items.filter((item) => item.status === 'SKIPPED').length} skipped`}
              .
            </p>
          </div>
          <Button onClick={onReady} className="bg-green-600 hover:bg-green-700">
            Choose a shipping service
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-none shadow-lg">
      <CardHeader className="border-b bg-gradient-to-r from-amber-50 to-orange-50">
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          {flagged.length} address{flagged.length === 1 ? '' : 'es'} need a look
        </CardTitle>
      </CardHeader>

      <CardContent className="p-6 space-y-4">
        <Alert className="border-amber-200 bg-amber-50">
          <AlertDescription className="text-amber-800">
            Nothing has been bought yet. Fix or skip each row below, then pick a service.
          </AlertDescription>
        </Alert>

        <ul className="space-y-4">
          {flagged.map((item) => (
            <li key={item.id}>
              <FlaggedRow
                item={item}
                pending={resolveMutation.isPending}
                onResolve={(body) => resolveMutation.mutate({ itemId: item.id, body })}
              />
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function FlaggedRow({ item, onResolve, pending }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    name: item.recipientName,
    address1: item.recipientAddress1,
    address2: item.recipientAddress2 ?? '',
    city: item.recipientCity,
    state: item.recipientState,
    zip: item.recipientZip,
    country: item.recipientCountry,
  });

  const suggestion = item.suggestedAddress;

  return (
    <div className="rounded-lg border border-amber-200 bg-white p-4 space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs text-slate-500">Row {item.rowIndex + 1}</p>
          <p className="font-semibold text-slate-900">{item.recipientName}</p>
        </div>
        <p className="text-sm text-amber-700 text-right max-w-sm">{item.validationMessage}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div className="rounded border bg-slate-50 p-3">
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">As entered</p>
          <p>{item.recipientAddress1}</p>
          {item.recipientAddress2 && <p>{item.recipientAddress2}</p>}
          <p>
            {item.recipientCity}, {item.recipientState} {item.recipientZip}
          </p>
        </div>

        {suggestion && (
          <div className="rounded border border-green-200 bg-green-50 p-3">
            <p className="text-xs uppercase tracking-wide text-green-600 mb-1">Carrier suggests</p>
            <p>{suggestion.address1}</p>
            {suggestion.address2 && <p>{suggestion.address2}</p>}
            <p>
              {suggestion.city}, {suggestion.state} {suggestion.zip}
            </p>
          </div>
        )}
      </div>

      {editing && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded border bg-slate-50 p-3">
          {[
            ['name', 'Name'],
            ['address1', 'Street address'],
            ['address2', 'Address line 2'],
            ['city', 'City'],
            ['state', 'State'],
            ['zip', 'ZIP'],
          ].map(([key, label]) => (
            <div key={key} className="space-y-1">
              <Label htmlFor={`${item.id}-${key}`} className="text-xs">
                {label}
              </Label>
              <Input
                id={`${item.id}-${key}`}
                value={draft[key]}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    [key]: key === 'state' ? e.target.value.toUpperCase() : e.target.value,
                  })
                }
                maxLength={key === 'state' ? 2 : undefined}
              />
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 justify-end">
        {suggestion && !editing && (
          <Button
            size="sm"
            disabled={pending}
            onClick={() =>
              onResolve({
                action: 'correct',
                recipient: {
                  name: item.recipientName,
                  address1: suggestion.address1,
                  address2: suggestion.address2 || null,
                  city: suggestion.city,
                  state: suggestion.state,
                  zip: suggestion.zip,
                  country: suggestion.country ?? 'US',
                },
              })
            }
            className="bg-green-600 hover:bg-green-700 gap-1"
          >
            <Check className="w-4 h-4" />
            Use suggestion
          </Button>
        )}

        {editing ? (
          <>
            <Button
              size="sm"
              disabled={pending}
              onClick={() =>
                onResolve({
                  action: 'correct',
                  recipient: { ...draft, address2: draft.address2 || null },
                })
              }
              className="bg-blue-600 hover:bg-blue-700 gap-1"
            >
              <ArrowRight className="w-4 h-4" />
              Save and use
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} disabled={pending}>
            Edit
          </Button>
        )}

        {/* For the case the validator is simply wrong — a new development, a
            rural route it does not know. The operator overrules it. */}
        <Button
          size="sm"
          variant="outline"
          disabled={pending}
          onClick={() => onResolve({ action: 'override' })}
        >
          Ship as entered
        </Button>

        <Button
          size="sm"
          variant="outline"
          disabled={pending}
          onClick={() => onResolve({ action: 'skip' })}
          className="gap-1 text-slate-600"
        >
          {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : <SkipForward className="w-4 h-4" />}
          Skip
        </Button>
      </div>
    </div>
  );
}
